import React, {
    type ComponentProps,
    PropsWithChildren,
    ReactNode
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Lozenge from "@atlaskit/lozenge";
import { media } from "@atlaskit/primitives/responsive";
import { B300, B400 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src/button";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { Icon } from "@/libraries/wpl/foundations/src/icon";
import {
    Features,
    PricingPlanComparison
} from "@/utils/pricing-queries/get-pricing-content-gql.ts";

import { ExpandableContent } from "../../../expandable-content/src/expandable-content";
import {
    CloudProductKey,
    COMPARE_FEATURES_CONTAINER,
    CONTACT_CTA_URL,
    CtaBtnText,
    Edition,
    SIGNUP_CTA_URL,
    SpecialPlanNames
} from "../price-finder/constants";
import { CtaButton } from "../price-finder/price-card/cta-button";
import { StickyCTAContextProvider } from "../price-finder/price-card/sticky-cta-context";

const tdStyles = css({
    "justifyContent": "space-between",
    "fontWeight": 400,
    "textAlign": "left",
    "color": wacToken("color.primary.black"),
    [media.below.sm]: {
        display: "flex",
        padding: "12px 14px"
    },
    "display": "table-cell",
    "padding": "14px",
    "verticalAlign": "baseline",
    "borderInlineEnd": `1px solid ${wacToken("color.neutral.light400")}`,
    ":last-of-type": {
        borderInlineEnd: "none"
    }
});

const advanceHeadingStyles = css({
    textAlign: "left"
});

const advanceTitleStyles = css({
    color: wacToken("color.primary.black"),
    fontWeight: "700"
});

const advanceButtontyles = css({
    marginBlockStart: "13px"
});

const advanceSoftStyles = css({
    color: wacToken("color.neutral.light600"),
    marginInlineStart: "6px"
});

const featureTableSubHeader = css({
    fontSize: "16px",
    lineHeight: "20px"
});

const featuresTableHeadIdPrefix = "featTableHead_";

interface FeaturesTableHeadCellProps
    extends React.HTMLProps<HTMLTableCellElement> {
    index: number;
}

interface PlanTier {
    title: string;
    price: string;
    button: string;
}

export type FeaturePlans = {
    [key: string]: TierCellContent;
};

interface PricingFeatureTableProps {
    title: string | undefined;
    comparePlans: PricingPlanComparison[] | undefined;
    compareFeatures: Features[] | undefined;
    linkValues?: {
        linkHref?: string | undefined;
        linkText?: string | undefined;
    };
    dataProductKey?: CloudProductKey;
    stickyCtasEnabled?: boolean;
}

interface PricingFeatureGroupProps {
    featureGroup: Features;
    orderedPlanTiers: string[];
    groupIndex: number;
}

export const FeaturesTableSection = (props: React.HTMLProps<HTMLElement>) => {
    return (
        <section
            css={css({
                "textAlign": "center",
                "margin": "auto",
                "width": "100%",
                [media.above.sm]: {
                    maxWidth: "1440px"
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                "> a": {
                    margin: "40px 0 64px",
                    fontWeight: 500
                }
            })}
            {...props}
        />
    );
};

export const FeaturesTableHeaderContainer = (
    props: React.HTMLProps<HTMLElement>
) => {
    return (
        <div
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: "16px"
            })}
        >
            {props.children}
        </div>
    );
};

export const FeaturesTableHeader = ({
    children,
    ...props
}: React.HTMLProps<HTMLHeadingElement>) => {
    return (
        <h2
            css={css({
                color: wacToken("color.primary.black"),
                fontWeight: 700,
                margin: 0,
                fontSize: "36px",
                [media.below.sm]: {
                    fontSize: "32px"
                },
                [media.above.md]: {
                    fontSize: "40px",
                    lineHeight: "48px"
                }
            })}
            {...props}
        >
            {children}
        </h2>
    );
};

export const FeaturesTableSubHeader = ({
    linkText,
    linkHref
}: {
    linkText?: string;
    linkHref?: string;
}) => {
    const intl = useIntl();

    const defaultLinkText = intl.formatMessage({
        id: "PricingPages.featureTable.subHeader",
        defaultMessage: "Learn about features"
    });

    const defaultLinkHref = `https://www.atlassian.com${useLocalizedHref(
        "/software/jira/features"
    )}`;

    return (
        <div css={featureTableSubHeader}>
            <Link
                text={linkText ?? defaultLinkText}
                href={linkHref ?? defaultLinkHref}
                className="link-class"
                type={"primary"}
                aria-label={linkText ?? defaultLinkText}
            />
        </div>
    );
};

const featuresTableGroupIdPrefix = "featTableGroup_";

const FeaturesTableContext = React.createContext<{
    planTiers: (string | PlanTier)[];
    groupHeaderId?: string;
}>({ planTiers: [] });

export const FeaturesTable = (
    props: PropsWithChildren<{
        captionText?: string;
        subCaptionText?: string;
        planTiers: (string | PlanTier)[];
        dataProductKey?: CloudProductKey;
        stickyCtasEnabled?: boolean;
    }>
) => {
    const intl = useIntl();
    const localizedSignupURL = useLocalizedHref(SIGNUP_CTA_URL);
    const localizedContactURL = useLocalizedHref(CONTACT_CTA_URL);
    return (
        <FeaturesTableContext.Provider value={{ planTiers: props.planTiers }}>
            <table
                css={css({
                    flexDirection: "column",
                    tableLayout: "fixed",
                    width: "100%",
                    padding: 0,
                    border: 0,
                    textAlign: "center",
                    borderCollapse: "collapse",
                    borderSpacing: 0,
                    [media.below.sm]: {
                        display: "flex",
                        marginBlockStart: "24px"
                    },
                    display: "table",
                    marginBlockStart: "32px",
                    [media.above.md]: {
                        marginBlockStart: "48px"
                    }
                })}
            >
                {props.captionText && (
                    <caption
                        css={css({
                            textAlign: "start",
                            padding: "32px 14px 14px",
                            fontFamily: wacToken("font.charlie.display"),
                            fontSize: "1.25rem",
                            [media.below.sm]: {
                                padding: "24px 14px 14px"
                            }
                        })}
                    >
                        {props.captionText}
                    </caption>
                )}
                {props.subCaptionText && (
                    <caption
                        css={css({
                            textAlign: "start",
                            padding: "0px 14px 14px 14px",
                            fontFamily: wacToken("font.charlie.display"),
                            fontSize: "1rem"
                        })}
                    >
                        {props.subCaptionText}
                    </caption>
                )}
                <thead
                    css={css({
                        "borderBlockEnd": `solid 1px ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        "th:last-of-type": {},
                        [media.below.sm]: {
                            display: "none"
                        },

                        "zIndex": 1,
                        "top": "68px", // for the sticky header, this is hardcoded to be the height of whatever is stickied to the page above this thead (e.g. the page navbar)
                        "position": "sticky",
                        "background": wacToken("color.neutral.light0")
                    })}
                >
                    <tr
                        css={css({
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                            "th:first-of-type": {
                                textTransform: "uppercase",
                                textAlign: "start"
                            }
                        })}
                    >
                        <FeaturesTableHeadCell
                            index={0}
                            role="presentation"
                            aria-hidden="true"
                        />
                        {props.planTiers.map((tierContent, index) => {
                            let edition,
                                ctaBtnText = "",
                                ctaBtnUrl = "",
                                dataEventPropsLabel = "start-trial";

                            if (
                                props.stickyCtasEnabled &&
                                props.dataProductKey
                            ) {
                                const startFreeTrialText = intl.formatMessage({
                                    id: "Pricing.CompareFeatures.StartFreeTrial",
                                    defaultMessage: CtaBtnText.START_FREE_TRIAL
                                });
                                const getItNowText = intl.formatMessage({
                                    id: "Pricing.CompareFeatures.GetItNow",
                                    defaultMessage: CtaBtnText.GET_IT_NOW
                                });
                                const contactSalesText = intl.formatMessage({
                                    id: "Pricing.CompareFeatures.ContactSales",
                                    defaultMessage: CtaBtnText.CONTACT_SALES
                                });

                                ctaBtnText = startFreeTrialText;
                                dataEventPropsLabel =
                                    dataEventPropsLabel = `${tierContent}-start-trial-compare-features`;

                                // tech-debt: get cta text and urls from contentful
                                // ticket: https://hello.jira.atlassian.cloud/browse/PRICING-1542
                                switch (tierContent) {
                                    case SpecialPlanNames.FREE:
                                        edition = Edition.FREE;
                                        ctaBtnText = getItNowText;
                                        ctaBtnUrl =
                                            localizedSignupURL +
                                            "?bundle=jira-software&edition=free";
                                        dataEventPropsLabel = `${tierContent}-get-it-now-compare-features`;
                                        break;

                                    case SpecialPlanNames.STANDARD:
                                        edition = Edition.STANDARD;
                                        ctaBtnUrl =
                                            localizedSignupURL +
                                            "?bundle=jira-software&edition=standard";
                                        break;

                                    case SpecialPlanNames.PREMIUM:
                                        edition = Edition.PREMIUM;
                                        ctaBtnUrl =
                                            localizedSignupURL +
                                            "?bundle=jira-software&edition=premium";
                                        break;

                                    case SpecialPlanNames.ENTERPRISE:
                                        edition = Edition.ENTERPRISE;
                                        ctaBtnText = contactSalesText;
                                        ctaBtnUrl = localizedContactURL;
                                        dataEventPropsLabel = `${tierContent}-contact-sales-compare-features`;
                                        break;
                                }
                            }
                            return (
                                <FeaturesTableHeadCell
                                    key={index}
                                    index={index}
                                    id={`${featuresTableHeadIdPrefix}${index}`}
                                >
                                    {typeof tierContent === "string" ? (
                                        <div
                                            css={css({
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "8px",
                                                alignItems: "flex-start"
                                            })}
                                        >
                                            <span css={advanceTitleStyles}>
                                                {tierContent}
                                            </span>
                                            {props.stickyCtasEnabled &&
                                                props.dataProductKey && (
                                                    <StickyCTAContextProvider
                                                        isSticky={undefined}
                                                    >
                                                        <CtaButton
                                                            edition={edition}
                                                            dataProductKey={
                                                                props.dataProductKey
                                                            }
                                                            type={"secondary"}
                                                            text={ctaBtnText}
                                                            dataEventPropsLabel={
                                                                dataEventPropsLabel
                                                            }
                                                            url={ctaBtnUrl}
                                                            isDisabled={false}
                                                            containerId={
                                                                COMPARE_FEATURES_CONTAINER
                                                            }
                                                        />
                                                    </StickyCTAContextProvider>
                                                )}
                                        </div>
                                    ) : tierContent ? (
                                        <div css={advanceHeadingStyles}>
                                            <span css={advanceTitleStyles}>
                                                {tierContent.title}
                                            </span>
                                            <div>
                                                <span css={advanceTitleStyles}>
                                                    {tierContent.price}
                                                </span>
                                                <span css={advanceSoftStyles}>
                                                    per user
                                                </span>
                                            </div>
                                            <Adg4Button
                                                css={advanceButtontyles}
                                                size="small"
                                                variant="secondary"
                                                color="black"
                                                colorAgainst="other"
                                                href="https://www.atlassian.com/enterprise/contact?formType=product-features"
                                            >
                                                {tierContent.button}
                                            </Adg4Button>
                                        </div>
                                    ) : (
                                        <span>Invalid Data</span>
                                    )}
                                </FeaturesTableHeadCell>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>{props.children}</tbody>
            </table>
        </FeaturesTableContext.Provider>
    );
};

const FeaturesTableHeadCell: React.FC<FeaturesTableHeadCellProps> = ({
    ...props
}) => {
    const isPresentation = props.role === "presentation";
    return (
        <th
            css={[tdStyles]}
            {...props}
            scope={isPresentation ? undefined : "col"}
        />
    );
};

type TierCellContent = { textContent?: string; checkmark?: boolean };

const lozengeContainerCss = css({
    marginInlineStart: "4px"
});

export const FeaturesTableRowHeader = (
    props: PropsWithChildren<{
        headerTitle: string;
        tierCellsContent: ReactNode[];
        groupId: string;
    }>
) => {
    return (
        <tr
            css={css({
                color: wacToken("color.primary.black"),
                flexDirection: "column",
                margin: "16px 0",
                backgroundColor: wacToken("color.neutral.light100"),
                transition: "background-color .5s",
                padding: 0,
                verticalAlign: "baseline",
                textAlign: "center",
                [media.below.sm]: {
                    display: "none"
                }
            })}
        >
            <th
                id={props.groupId}
                scope="rowgroup"
                colSpan={props.tierCellsContent.length + 1}
                css={[tdStyles, css({ textAlign: "start" })]}
            >
                <span
                    css={css({
                        color: wacToken("color.primary.black"),
                        width: "100%",
                        fontWeight: "bold",
                        height: "auto",
                        display: "block",
                        overflowWrap: "break-word"
                    })}
                >
                    {props.headerTitle}
                </span>
            </th>
        </tr>
    );
};

interface FeaturesTableRowExpanderV2Props {
    text: string;
    expanderContent: string | null;
    href: string | null;
}

export const FeaturesTableRowV2 = (
    props: PropsWithChildren<{
        featureCellContent: FeaturesTableRowExpanderV2Props;
        tierCellsContent: TierCellContent[];
        lozengeText?: string | null;
        id: string;
    }>
) => {
    let lozenge: ReactNode = null;
    const { text, expanderContent, href } = props.featureCellContent;
    if (props.lozengeText) {
        lozenge = (
            <span css={lozengeContainerCss}>
                <Lozenge>{props.lozengeText}</Lozenge>
            </span>
        );
    }

    const rowDescId = `rowDesc_${props.id}`;

    let featureCellContent: JSX.Element;
    if (href) {
        featureCellContent = (
            <>
                <Link
                    css={css({
                        fontSize: "14px",
                        marginInlineStart: "4px"
                    })}
                    text={text}
                    href={href}
                    type={"primary"}
                    aria-label={text}
                />
                {lozenge}
            </>
        );
    } else {
        const children = expanderContent ? (
            <div
                css={css({
                    "fontSize": "14px",
                    "color": `${wacToken("color.primary.black")} !important`,
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& a": {
                        "color": B400,
                        "textDecoration": "underline",
                        "&:hover": {
                            color: B300,
                            textDecoration: "none"
                        }
                    }
                })}
                dangerouslySetInnerHTML={{ __html: expanderContent }}
            />
        ) : undefined;

        featureCellContent = (
            <ExpandableContent
                eventDataName={text}
                label={
                    <span>
                        {text}
                        {lozenge}
                    </span>
                }
            >
                {children}
            </ExpandableContent>
        );
    }

    return (
        <tr
            css={css({
                "margin": "16px 0",
                "backgroundColor": wacToken("color.neutral.light0"),
                "transition": "background-color .5s",
                "padding": 0,
                "verticalAlign": "baseline",
                "textAlign": "center",
                ":last-of-type": {
                    marginBlockEnd: "0"
                },
                "&:hover": {
                    backgroundColor: wacToken("color.neutral.light100")
                },
                [media.below.sm]: {
                    border: `solid 1px ${wacToken("color.neutral.light400")}`,
                    borderRadius: "20px",
                    display: "flex",
                    flexDirection: "column"
                }
            })}
        >
            <th
                id={rowDescId}
                scope="row"
                css={[
                    tdStyles,
                    css({
                        textAlign: "start",
                        fontWeight: "normal",
                        [media.below.sm]: {
                            borderStartStartRadius: "20px",
                            borderStartEndRadius: "20px",
                            background: wacToken("color.neutral.light200"),
                            padding: "14px"
                        }
                    })
                ]}
            >
                <span
                    css={css({
                        width: "100%",
                        overflowX: "hidden"
                    })}
                >
                    {featureCellContent}
                </span>
            </th>
            {props.tierCellsContent.map((content, index) => (
                <FeaturesTableCell
                    index={index}
                    key={index}
                    cellContent={content}
                    rowDescId={rowDescId}
                />
            ))}
        </tr>
    );
};

export const FeaturesTableCell = ({
    cellContent,
    index,
    rowDescId
}: {
    cellContent: TierCellContent;
    index: number;
    rowDescId: string;
}) => {
    const { planTiers, groupHeaderId } = React.useContext(FeaturesTableContext);
    const tierContent = planTiers[index];
    const colHeaderId = `${featuresTableHeadIdPrefix}${index}`;
    const headersAttr = [groupHeaderId, colHeaderId, rowDescId]
        .filter(Boolean)
        .join(" ");

    const cellContentStyles = css({
        display: "flex",
        gap: token("space.100")
    });

    return (
        <td
            css={[tdStyles, css({ verticalAlign: "top" })]}
            headers={headersAttr}
        >
            <span
                css={css({
                    [media.below.sm]: {
                        display: "flex"
                    },
                    display: "none"
                })}
            >
                {typeof tierContent === "string"
                    ? tierContent
                    : tierContent && <>{tierContent.title}</>}
            </span>
            {cellContent && (
                <div css={cellContentStyles}>
                    {cellContent.checkmark && (
                        <Checkmark
                            containerProps={{
                                "aria-hidden": "true",
                                "style": { marginBlockStart: "-6px" }
                            }}
                        />
                    )}
                    {cellContent.textContent && (
                        <span
                            css={css({
                                "textAlign": "start",
                                "fontSize": "14px",
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                "& a": {
                                    "color": B400,
                                    "textDecoration": "underline",
                                    "&:hover": {
                                        color: B300,
                                        textDecoration: "none"
                                    }
                                }
                            })}
                            dangerouslySetInnerHTML={{
                                __html: cellContent.textContent
                            }}
                        />
                    )}
                </div>
            )}
        </td>
    );
};

type CheckmarkProps = {
    containerProps?: ComponentProps<"div">;
};

export const Checkmark = (props: CheckmarkProps) => {
    return (
        <div
            {...props.containerProps}
            css={css({
                [media.below.sm]: {
                    flexBasis: "50%"
                }
            })}
        >
            <span
                css={css({
                    backgroundColor: wacToken("color.extended.lime700"),
                    borderRadius: "50%",
                    color: wacToken("color.neutral.light0"),
                    display: "inline-block",
                    flexShrink: 0,
                    fontWeight: "bold",
                    lineHeight: "24px",
                    margin: "4px",
                    textAlign: "center",
                    height: "24px",
                    width: "24px"
                })}
                data-testid="checkmark"
            >
                <Icon
                    name="featurecheckmark"
                    size="xs"
                    color={wacToken("color.neutral.light0")}
                />
            </span>
        </div>
    );
};

export const PricingFeatureTable = ({
    title,
    comparePlans,
    compareFeatures,
    linkValues,
    dataProductKey,
    stickyCtasEnabled
}: PricingFeatureTableProps) => {
    const intl = useIntl();

    if (!comparePlans || !compareFeatures) {
        return null;
    }

    // maintain a list to keep track of the plans in the contentful order
    const orderedPlanTiers =
        comparePlans?.map((plan) => plan.planTitleExternal ?? "") ?? [];

    const fallbackTitle = intl.formatMessage({
        id: "PricingFeatureTable.fallbackTitle",
        defaultMessage: "Compare Features"
    });

    return (
        <FeaturesTableSection>
            <FeaturesTableHeaderContainer>
                <FeaturesTableHeader>
                    {title ?? fallbackTitle}
                </FeaturesTableHeader>
                {linkValues?.linkHref && linkValues?.linkText ? (
                    <FeaturesTableSubHeader {...linkValues} />
                ) : null}
            </FeaturesTableHeaderContainer>
            <FeaturesTable
                captionText=""
                planTiers={orderedPlanTiers}
                dataProductKey={dataProductKey}
                stickyCtasEnabled={stickyCtasEnabled}
            >
                {compareFeatures?.map((featureGroup, index) => {
                    return (
                        <PricingFeatureGroup
                            featureGroup={featureGroup}
                            orderedPlanTiers={orderedPlanTiers}
                            key={featureGroup.featureGroupTitleExternal}
                            groupIndex={index}
                        />
                    );
                })}
            </FeaturesTable>
        </FeaturesTableSection>
    );
};

export const PricingFeatureGroup = ({
    featureGroup,
    orderedPlanTiers,
    groupIndex
}: PricingFeatureGroupProps) => {
    const { featureGroupTitleExternal, features } = featureGroup;
    const { planTiers } = React.useContext(FeaturesTableContext);
    const groupId = `${featuresTableGroupIdPrefix}${groupIndex}`;
    return (
        <FeaturesTableContext.Provider
            value={{ planTiers, groupHeaderId: groupId }}
        >
            <FeaturesTableRowHeader
                headerTitle={featureGroupTitleExternal ?? ""}
                tierCellsContent={Array(
                    Object.keys(orderedPlanTiers).length
                ).fill("")}
                groupId={groupId}
            />

            {features?.map((feature, index) => {
                const {
                    featureNameExternal,
                    description,
                    relevantUrl,
                    callOut,
                    featureAdditionalInformation,
                    relevantPlan
                } = feature;

                // Initialize an object to store feature cell information for each plan.
                // The keys will be the plan names, and the values will be initially set to exmpty objects.
                const featureCellInfo = orderedPlanTiers.reduce<FeaturePlans>(
                    (obj, plan) => {
                        obj[plan] = {};
                        return obj;
                    },
                    {}
                );

                if (featureAdditionalInformation) {
                    // Iterate over each addtional information and update the appropiriate featureCellInfo with corresponding messages
                    featureAdditionalInformation?.forEach((info) => {
                        const {
                            featureAdditionalInformation: message,
                            relevantPlan: featureRelevantPlans
                        } = info;
                        featureRelevantPlans?.forEach((plan) => {
                            const planTitleExternal =
                                plan.planTitleExternal as string;
                            if (featureCellInfo[planTitleExternal]) {
                                featureCellInfo[planTitleExternal].textContent =
                                    message ?? "";
                            }
                        });
                    });
                }
                // hanlding boolean values
                if (relevantPlan) {
                    relevantPlan?.forEach((plan) => {
                        // Mark this plan as true in featureCellInfo
                        const planTitleExternal =
                            plan.planTitleExternal as string;
                        if (featureCellInfo[planTitleExternal]) {
                            featureCellInfo[planTitleExternal].checkmark = true;
                        }
                    });
                }

                // Generate the ordered values for tierCellsContent
                const orderedValues = orderedPlanTiers
                    .map((key) => featureCellInfo[key])
                    .filter((value) => value !== undefined);

                return (
                    <FeaturesTableRowV2
                        featureCellContent={{
                            text: featureNameExternal ?? "",
                            expanderContent: description ?? null,
                            href: relevantUrl ?? null
                        }}
                        lozengeText={callOut}
                        tierCellsContent={orderedValues}
                        key={featureNameExternal}
                        id={`${groupIndex}_${index}`}
                    />
                );
            })}
        </FeaturesTableContext.Provider>
    );
};
